import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  Trash2, 
  Key, 
  Building, 
  Users, 
  UserPlus, 
  ClipboardList,
  AlertTriangle,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import logger from '../utils/logger';

const AdminPanel = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const isSuperAdmin = user?.rol === 'super_admin';
  const isCompanyAdmin = user?.rol === 'company_admin';

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      setLoading(true);
      
      if (isSuperAdmin) {
        // Super admin can see all data
        await Promise.all([
          loadCompanies(),
          loadAllEmployees(),
          loadAllCandidates(),
          loadAllAssessments()
        ]);
      } else if (isCompanyAdmin) {
        // Company admin can see only their company data
        await Promise.all([
          loadCompanyEmployees(),
          loadCompanyCandidates(),
          loadCompanyAssessments()
        ]);
      }
    } catch (error) {
      logger.error('Error loading admin data:', error);
      toast.error(t('admin.error_loading_data', 'Error loading data'));
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await axios.get(`/admin/companies`);
      if (response.data.success) {
        setCompanies(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading companies:', error);
    }
  };

  const loadAllEmployees = async () => {
    try {
      const response = await axios.get(`/admin/users?role=employee`);
      if (response.data.success) {
        setEmployees(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading employees:', error);
    }
  };

  const loadAllCandidates = async () => {
    try {
      const response = await axios.get(`/admin/users?role=candidate`);
      if (response.data.success) {
        setCandidates(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading candidates:', error);
    }
  };

  const loadAllAssessments = async () => {
    try {
      const response = await axios.get(`/admin/assessments`);
      if (response.data.success) {
        setAssessments(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading assessments:', error);
    }
  };

  const loadCompanyEmployees = async () => {
    try {
      const response = await axios.get(`/companies/employees`);
      if (response.data.success) {
        setEmployees(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading company employees:', error);
    }
  };

  const loadCompanyCandidates = async () => {
    try {
      const response = await axios.get(`/companies/candidates`);
      if (response.data.success) {
        setCandidates(response.data.data.candidates);
      }
    } catch (error) {
      logger.error('Error loading company candidates:', error);
    }
  };

  const loadCompanyAssessments = async () => {
    try {
      const response = await axios.get(`/assessments`);
      if (response.data.success) {
        setAssessments(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading company assessments:', error);
    }
  };

  const confirmDelete = (type, item, action) => {
    const messages = {
      company: t('admin.delete_company_confirmation', 'Are you sure you want to delete this company and all associated data?'),
      user: t('admin.delete_user_confirmation', 'Are you sure you want to delete this user and all their assessments?'),
      assessment: t('admin.delete_assessment_confirmation', 'Are you sure you want to delete this assessment?')
    };

    if (window.confirm(messages[type])) {
      action();
    }
  };

  const deleteCompany = async (companyId) => {
    try {
      const response = await axios.delete(`/admin/companies/${companyId}`);
      if (response.data.success) {
        toast.success(t('admin.deleted_successfully', 'Deleted successfully'));
        loadAdminData();
      }
    } catch (error) {
      toast.error(t('admin.delete_error', 'Error deleting item'));
      logger.error('Error deleting company:', error);
    }
  };

  const deleteUser = async (userId) => {
    try {
      const response = await axios.delete(`/admin/users/${userId}`);
      if (response.data.success) {
        toast.success(t('admin.deleted_successfully', 'Deleted successfully'));
        loadAdminData();
      }
    } catch (error) {
      toast.error(t('admin.delete_error', 'Error deleting item'));
      logger.error('Error deleting user:', error);
    }
  };

  const deleteAssessment = async (assessmentId) => {
    try {
      const response = await axios.delete(`/admin/assessments/${assessmentId}`);
      if (response.data.success) {
        toast.success(t('admin.deleted_successfully', 'Deleted successfully'));
        loadAdminData();
      }
    } catch (error) {
      toast.error(t('admin.delete_error', 'Error deleting item'));
      logger.error('Error deleting assessment:', error);
    }
  };

  const openPasswordModal = (userItem) => {
    setSelectedUser(userItem);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const changePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error(t('admin.password_min_length', 'Password must be at least 6 characters'));
      return;
    }

    try {
      const response = await axios.put(
        `/admin/users/${selectedUser.id}/change-password`,
        { new_password: newPassword }
      );
      
      if (response.data.success) {
        toast.success(t('admin.password_changed_successfully', 'Password changed successfully'));
        setShowPasswordModal(false);
        setSelectedUser(null);
        setNewPassword('');
      }
    } catch (error) {
      toast.error(t('admin.password_change_error', 'Error changing password'));
      logger.error('Error changing password:', error);
    }
  };

  if (!isSuperAdmin && !isCompanyAdmin) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-8 text-center">
            <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('admin.access_denied', 'Access Denied')}
            </h2>
            <p className="text-gray-600">
              {t('admin.admin_required', 'Administrator privileges required to access this page')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('loading', 'Loading...')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Shield className="h-6 w-6 mr-2" />
            {isSuperAdmin ? t('admin.super_admin_panel', 'Super Admin Panel') : t('admin.company_admin_panel', 'Company Admin Panel')}
          </CardTitle>
          <CardDescription>
            {isSuperAdmin 
              ? t('admin.super_admin_desc', 'Manage all system data including companies, users, and assessments')
              : t('admin.company_admin_desc', 'Manage your company\'s employees, candidates, and assessments')
            }
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Companies Section (Super Admin Only) */}
      {isSuperAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Building className="h-5 w-5 mr-2" />
              {t('admin.companies', 'Companies')} ({companies.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {companies.map((company) => (
                <div key={company.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-medium">{company.nombre}</h3>
                    <p className="text-sm text-gray-600">{company.industria}</p>
                    <Badge variant="outline">{company.codigo_acceso}</Badge>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => confirmDelete('company', company, () => deleteCompany(company.id))}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    {t('admin.delete_company', 'Delete Company')}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Employees Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Users className="h-5 w-5 mr-2" />
            {t('admin.employees', 'Employees')} ({employees.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {employees.map((employee) => (
              <div key={employee.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h3 className="font-medium">{employee.nombre}</h3>
                  <p className="text-sm text-gray-600">{employee.email}</p>
                  <p className="text-sm text-gray-500">{employee.departamento}</p>
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openPasswordModal(employee)}
                  >
                    <Key className="h-4 w-4 mr-1" />
                    {t('admin.change_password', 'Change Password')}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => confirmDelete('user', employee, () => deleteUser(employee.id))}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    {t('admin.delete_user', 'Delete User')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Candidates Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <UserPlus className="h-5 w-5 mr-2" />
            {t('admin.candidates', 'Candidates')} ({candidates.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {candidates.map((candidate) => (
              <div key={candidate.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h3 className="font-medium">{candidate.nombre || candidate.name}</h3>
                  <p className="text-sm text-gray-600">{candidate.email}</p>
                  <Badge variant="secondary">
                    {candidate.completed_assessments} {t('admin.assessments_completed', 'assessments completed')}
                  </Badge>
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openPasswordModal(candidate)}
                  >
                    <Key className="h-4 w-4 mr-1" />
                    {t('admin.change_password', 'Change Password')}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => confirmDelete('user', candidate, () => deleteUser(candidate.id))}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    {t('admin.delete_user', 'Delete User')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Assessments Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <ClipboardList className="h-5 w-5 mr-2" />
            {t('admin.assessments', 'Assessments')} ({assessments.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {assessments.map((assessment) => (
              <div key={assessment.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h3 className="font-medium">{assessment.tipo}</h3>
                  <p className="text-sm text-gray-600">
                    {t('admin.user', 'User')}: {assessment.usuario_nombre}
                  </p>
                  <div className="flex items-center space-x-2 mt-1">
                    <Badge variant={assessment.status === 'completado' ? 'default' : 'secondary'}>
                      {assessment.status}
                    </Badge>
                    <span className="text-sm text-gray-500">
                      {assessment.progreso_porcentaje}%
                    </span>
                  </div>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => confirmDelete('assessment', assessment, () => deleteAssessment(assessment.id))}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  {t('admin.delete_assessment', 'Delete Assessment')}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Key className="h-5 w-5 mr-2" />
                {t('admin.change_password', 'Change Password')}
              </CardTitle>
              <CardDescription>
                {t('admin.changing_password_for', 'Changing password for')}: {selectedUser?.nombre || selectedUser?.name}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="new-password">{t('admin.new_password', 'New Password')}</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t('admin.password_placeholder', 'Minimum 6 characters')}
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setShowPasswordModal(false)}
                >
                  {t('cancel', 'Cancel')}
                </Button>
                <Button onClick={changePassword}>
                  {t('admin.change_password', 'Change Password')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;